import React from 'react';
import { Users, Plus, Search, Loader2, User, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';

const LIFECYCLE_STAGES = [
  { value: 'lead', label: 'Lead', color: 'bg-blue-100 text-blue-800' },
  { value: 'prospect', label: 'Prospect', color: 'bg-purple-100 text-purple-800' },
  { value: 'first_time', label: 'First Time', color: 'bg-green-100 text-green-800' },
  { value: 'repeat', label: 'Repeat', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'loyal', label: 'Loyal', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'at_risk', label: 'At Risk', color: 'bg-orange-100 text-orange-800' },
  { value: 'churned', label: 'Churned', color: 'bg-red-100 text-red-800' }
];

const getStageColor = (stage) => {
  const found = LIFECYCLE_STAGES.find(s => s.value === stage);
  return found?.color || 'bg-gray-100 text-gray-800';
};

export { LIFECYCLE_STAGES, getStageColor };

export const CustomerList = ({ 
  customers, isLoading, search, setSearch, 
  stageFilter, setStageFilter, onViewCustomer, onAddCustomer 
}) => {
  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, phone, or email..."
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="crm-search-input"
              />
            </div>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="crm-stage-filter">
                <SelectValue placeholder="Lifecycle Stage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {LIFECYCLE_STAGES.map(stage => (
                  <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Customer List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#039fff]" />
        </div>
      ) : customers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No customers found</p>
            <Button onClick={onAddCustomer} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Add Customer
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {customers.map((customer) => (
            <Card 
              key={customer.customer_id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => onViewCustomer(customer)}
              data-testid={`customer-card-${customer.customer_id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#001A47] flex items-center justify-center">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold">{customer.name}</p>
                      <p className="text-sm text-gray-500">{customer.mobile}</p>
                    </div>
                  </div>
                  <Badge className={getStageColor(customer.lifecycle_stage)}>
                    {customer.lifecycle_stage}
                  </Badge>
                </div>
                
                {customer.email && (
                  <p className="text-sm text-gray-500 mb-2">{customer.email}</p>
                )}
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Orders: {customer.total_orders || 0}</span>
                  {customer.lead_score > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 text-yellow-500" />
                      <span>{customer.lead_score}</span>
                    </div>
                  )}
                </div>

                {customer.tags && customer.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {customer.tags.slice(0, 3).map((tag, i) => (
                      <Badge key={`${tag}-${i}`} variant="secondary" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
